import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import type { ReviewState } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { useConstraint, useConstraintRegistry } from '@chess/api-constraints/react';
import { Scoresheet } from './Scoresheet';
import { sheetView } from './sheet.ts';
import { keepInView } from './scroll.ts';
import { sheetHeading } from './puzzle-rows.ts';
import { useReview } from './use-review.ts';
import { selectMove } from './select-move.ts';
import './scoresheet.css';

// The scoresheet panel, filling the `review.moveList` slot. The moves are the
// review's game (`ReviewState.game`, parsed once by the review screen); the
// marks beside them come from the review's scan (`ReviewState.scan`, which
// carries the user's mistake rules), graded move by move as it grows. What
// the sheet shows for a state is decided in `sheet.ts` and `rows.ts`.

export function setup(app: PiletApi) {
  const Panel: React.FC = () => {
    const api = useReview(app);
    const state = useSelect(api, (s) => s) ?? IDLE;
    // The board's constraints: Scoresheet visibility conceals the moves from
    // a ply on (a puzzle's answer), Controls may switch the clicks off.
    const registry = useConstraintRegistry(app);
    const hideFrom = useConstraint(registry, state.scope, 'scoresheetVisibility').hideFrom;
    const scoresheetClicks = useConstraint(registry, state.scope, 'controls').scoresheetClicks;
    const view = React.useMemo(() => sheetView(state, { hideFrom, scoresheetClicks }), [state, hideFrom, scoresheetClicks]);
    const { status, moves, judgements, branchPly, currentPly, lineLength, concealFrom } = view;
    // Choosing a move drops any line the reader was exploring, shows the
    // position the move was played from (2026-10-13; every link to a move in
    // the app lands there: the board shows what the player saw, the arrows the
    // move and the engine's) and a beat later makes the move, animated
    // (2026-10-15, `select-move.ts`). A new click, or leaving, cancels the
    // pending move. Not while the Controls constraint says no.
    const pendingMove = React.useRef<() => void>(() => {});
    const onSelect = React.useCallback(
      (ply: number) => {
        if (!api) return;
        pendingMove.current();
        pendingMove.current = selectMove(api, ply);
      },
      [api],
    );
    React.useEffect(() => () => pendingMove.current(), []);
    // Keep the current move, or while exploring the move the line branches
    // from, inside the panel's own scroll box. Only the panel scrolls, never
    // the page around it, and it re-runs when the moves arrive after the ply.
    const sheetRef = React.useRef<HTMLElement>(null);
    React.useLayoutEffect(() => {
      const panel = sheetRef.current;
      if (!panel) return;
      // The starting position has no move to show: back to the top of the sheet.
      if (currentPly === 0 && branchPly === undefined) {
        panel.scrollTop = 0;
        return;
      }
      const cell = panel.querySelector<HTMLElement>('.scoresheet-current') ?? panel.querySelector<HTMLElement>('.scoresheet-branch');
      if (cell) keepInView(panel, cell);
    }, [currentPly, branchPly, lineLength, status, moves.length, concealFrom]);
    return (
      <section className="panel review-move-list panel-grow" ref={sheetRef}>
        <h2 className={concealFrom !== undefined ? 'review-move-list-puzzle' : undefined}>{sheetHeading(concealFrom)}</h2>
        {view.message !== undefined && <p className="panel-empty">{view.message}</p>}
        {status === 'ready' && (
          <Scoresheet
            moves={moves}
            judgements={judgements}
            branchPly={branchPly}
            concealFrom={concealFrom}
            currentPly={currentPly}
            onSelect={view.clickable ? onSelect : undefined}
          />
        )}
      </section>
    );
  };
  app.registerExtension('review.moveList', Panel);
}

const IDLE: ReviewState = { status: 'idle', ply: 0 };
